import type {
    AcceptConsentRequest,
    AccountSingleStepPinRequest,
    AddressDetailsRequest,
    AddressDetailsResponse,
    ApplicationScoringRequest,
    ApplicationScoringResponse,
    AuthorizationCodeResponse,
    AuthorizationStatusResponse,
    AuthResponse,
    CancelTransactionRequest,
    ChangePinRequest,
    CheckoutStateMachineStatusDto,
    CheckoutStepRecordRequest,
    CompaniesListDTO,
    ConsentV2Request,
    ContractDataComplexDTO,
    ContractSignOrBuyRequest,
    CountryCodebookDTO,
    CreatePinRequest,
    CreditPaymentDataResponse,
    CreditPaymentPlanRequest,
    CreditPurchaseRangeResponse,
    CreditTokenValidationResponse,
    CurrencyInfo,
    EligibilityValidationResponse,
    EmploymentInfoRequest,
    EmploymentMetaTypeDTO,
    ExecutionAmountMetaDTO,
    FinancialAccountNumberV2Request,
    FinancialDataResponse,
    FinancialDetailsCheckRequest,
    FinancialDetailsOptionsTypes,
    FinancialDetailsRequestV2,
    FinancialDetailsResponse,
    FinancialDetailsValidationDataDTO,
    FinancialProductV2Request,
    GetSelfCheckoutTokenRequest,
    GplCheckoutInitResponse,
    HandoffExchangeRequest,
    HandoffExchangeResponse,
    HandoffIssueRequest,
    HandoffIssueResponse,
    IbanDataRequest,
    IbanDataResponse,
    JWTLoginRequest,
    LoanAmountLimitsRequest,
    MaritalStatus,
    MaritalStatusRequest,
    MaritalStatusResponse,
    MplCheckoutStatusResponse,
    MplPaymentDataRequest,
    NationalityCodebookDTO,
    OfferPlanDataRequest,
    OfferValidationResultDTO,
    Page,
    PaymentData,
    PaymentDataDTO,
    PaymentDataRequest,
    PaymentPlan,
    PaymentPlanDTO,
    PersonalDetailsCheckRequest,
    PersonalDetailsDTO,
    PersonalDetailsV2Request,
    PersonMarketingConsentChangeRequest,
    PostZipCodebookDTO,
    PreQualifiedInfoDTO,
    PreQualifiedInfoRequest,
    ProfileAddressChangeRequest,
    ProfileIbanChangeRequest,
    QuestionnaireDTO,
    QuestionnaireListDTO,
    QuestionnaireRequest,
    RecordStepParams,
    ReCreatePinRequest,
    RequestFinancialDataRequest,
    SecurityConfirmationRequest,
    SessionInfoDTO,
    SettlementCodebookDTO,
    SignOrBuyResponse,
    StateMachineDto,
    StreetCodebookDTO,
    TransactionRequest,
    UpsellCheckoutInitRequest,
    UpsellCheckoutInitResponse,
    UpsellOfferStateDto,
    ValidateOfferInitialResponse,
    ValidateOfferResultResponse,
    ValidateVendorResponse,
    ValidationDataDTO,
    ValidationDataResultRequest,
    ValidationDataV2DTO,
    VendorApiInitResponse,
    VendorApiPreQualifiedRequest,
    VendorProductValidationRequest,
    VendorRequestInvalidateRequest,
} from '@leanpay/models'
import { HttpClient } from '@leanpay/http-client'
import { storage } from '@leanpay/local-storage'
import {
    FinancialDataStatus,
    ScoringStatus,
    toAddressData,
    toConfirmedPaymentPlan,
    toOfferValidation,
    toPaymentData,
    toQuestionnaire,
} from '@leanpay/models'
import { STORAGE_KEY } from '@leanpay/utils'

import { isStandardErrorResponse, preQualifiedErrorHandler } from '../errorHandler'
import { clientInterceptor } from '../interceptors'

// Raw wire shape of api/mpl/v1/payment-data (legacy MPL). Dates arrive as strings and a few
// checkout-only PaymentPlan fields are absent; mapMplPaymentDataToPaymentData normalizes both so
// the existing PaymentData / PaymentPlan types are reused unchanged (no new Mpl* response DTOs).
type MplPaymentPlanWire = Omit<
    PaymentPlanDTO,
    | 'firstInstallmentDate'
    | 'requiresCreditReviewFeeConsent'
    | 'totalLoanAmount'
    | 'insurancePremiumFee'
> & {
    firstInstallmentDate: string
    requiresCreditReviewFeeConsent?: boolean | null
    totalLoanAmount?: number
    insurancePremiumFee?: number
}

type MplPaymentDataWire = Omit<PaymentDataDTO, 'paymentCycleDates' | 'paymentPlans'> & {
    paymentCycleDates?: string[]
    paymentPlans: MplPaymentPlanWire[]
}

const toTimestamp = (value: string): number => new Date(value).getTime()

// paymentCycleDateStrings keeps the server's original cycle-date strings (same order as
// paymentData.paymentCycleDates) so the picked one is echoed back verbatim on a cycle re-fetch.
export interface MplPaymentData {
    paymentData: PaymentData
    paymentCycleDateStrings: string[]
}

const mapMplPaymentDataToPaymentData = (wire: MplPaymentDataWire): PaymentData =>
    toPaymentData({
        ...wire,
        paymentCycleDates: (wire.paymentCycleDates ?? []).map(toTimestamp),
        paymentPlans: wire.paymentPlans.map((plan) => ({
            ...plan,
            firstInstallmentDate: toTimestamp(plan.firstInstallmentDate),
            // Checkout-only fields the MPL wire omits; defaulted and never read once the SLO
            // insurance UI is stripped (no CPI insurance on SLO Cash Loans).
            requiresCreditReviewFeeConsent: plan.requiresCreditReviewFeeConsent ?? null,
            totalLoanAmount: plan.totalLoanAmount ?? 0,
            insurancePremiumFee: plan.insurancePremiumFee ?? 0,
        })),
    })

// ~60 s cap: on a premature frontend drop (lockstep release, no feature flag) the timeout surfaces
// the error screen instead of an endless spinner.
export const BACKEND_CHECKS_POLL_MAX_ATTEMPTS = 20
export const BACKEND_CHECKS_POLL_INTERVAL_MS = 3000
// PLAN_DATA covers the window before the backend enters the first auto-executed check.
const BACKEND_DRIVEN_PENDING_STATES = [
    'PLAN_DATA',
    'VALIDATE_OFFER',
    'WATCHLIST',
    'SCORING_DECISION_V1',
    'SCORING_CONFIRMATION',
    'VALIDATION_RESULTS',
]
const VALIDATE_OFFER_FAILURE_STATE = 'VALIDATE_OFFER_FAILURE'
// The backend reports the two scoring outcomes separately: a negative decision (business
// rejection) and a failed scoring call (exception, or an async failure callback with no decision).
const SCORING_DECISION_FAILURE_STATE = 'SCORING_DECISION_FAILURE'
const SCORING_REQUEST_FAILURE_STATE = 'SCORING_REQUEST_FAILURE'
// The validation step found no possible cash flow. Its sibling ADJUSTED_PLAN_DATA is the branch
// where the product allows an adjusted plan instead — a manual state, so it settles the poll too.
const VALIDATION_RESULTS_FAILURE_STATE = 'VALIDATION_RESULTS_FAILURE'
const ADJUSTED_PLAN_DATA_STATE = 'ADJUSTED_PLAN_DATA'
// Mirrors CreditFailureReason.Generic, which this package cannot import from the credit app.
const GENERIC_DECLINE_REASON = 'GENERIC'
// Mirrors CreditFailureReason.FailCreditCheck: a negative decision renders as the credit-check
// rejection.
const SCORING_DECISION_DECLINE_REASON = 'FAIL_CREDIT_CHECK'
// Mirrors CreditFailureReason.ScoringFailed: a technical failure renders as the "our system has a
// problem, try later" copy, never as a rejection the user could read as a decision.
const SCORING_REQUEST_FAILURE_REASON = 'SCORING_FAILED'
// Mirrors CreditFailureReason.NoPaymentOptions. Shared by the no-possible-plan decline and the
// adjustment branch: the adjustment screen does not exist on this flow, so parity with today means
// the customer sees the same no-options copy rather than waiting on a spinner.
const NO_POSSIBLE_PLAN_DECLINE_REASON = 'NO_PAYMENT_OPTIONS'

// stateMachineState travels with the reason because the two are not interchangeable: the caller
// needs the state to decide whether the failure still awaits its confirmation, and one reason maps
// to two states (NO_PAYMENT_OPTIONS is confirmable as a failure, but not as ADJUSTED_PLAN_DATA).
export type InitialAssessment =
    | { outcome: 'DECLINED'; reason: string; stateMachineState?: string }
    | { outcome: 'VALIDATED'; plan: PaymentPlan }

class CheckoutService {
    _httpClient = new HttpClient({
        config: {
            baseURL: undefined,
            responseType: 'json',
        },
        interceptorConfig: {
            onRequestFulfilled: clientInterceptor as any,
            onRejected: preQualifiedErrorHandler,
        },
    })

    private _financialProductInitUrl = 'api/vendor/financial-product-init'
    private _mplPaymentDataUrl = 'api/mpl/v1/payment-data'
    private _mplPurchaseLimitUrl = 'api/mpl/v1/purchase-limit'
    private _mplCheckoutStatusUrl = 'api/checkout/checkout-status'
    private _financialTypes = 'api/checkout/financial-types'
    private _employmentTypesUrl = 'api/checkout/employment-types'
    private _maritalStatusesUrl = 'api/checkout/marital-statuses'
    private _maritalStatusUrl = 'api/checkout/marital-status'

    private _submitEmploymentInfo = '/api/checkout/employment-info'
    private _checkFinancialDetailsUrl = 'api/checkout/check-financial-details'

    private _initScoringUrl = 'api/checkout/v1/application-scoring/init'
    private _applicationScoringUrl = 'api/checkout/v1/application-scoring'
    private _validationResultUrl = 'api/checkout/v1/validation-result'
    private _eligibilityValidationResultUrl = 'api/mpl/v1/validator/validation-result'
    private _offerValidationResultUrl = 'api/offer'
    private _amlWatchListUrl = '/api/checkout/v2/watchlist'
    private _stateMachineUrl = '/api/checkout/state-machine'
    private _checkoutStateMachineStatusUrl = 'api/checkout/state-machine-status'

    private _requestPinUrl = 'api/checkout/request-pin'
    private _resendOtpUrl = 'api/account/single-step/resend-pin'
    private _resendOtpOverVoiceUrl = 'api/account/single-step/resend-pin-over-voice'
    private _authenticateUrl = 'api/authenticate'
    private _issueHandoffUrl = 'api/auth/handoff'
    private _exchangeHandoffUrl = 'api/auth/handoff/exchange'

    private _recordStepUrl = 'api/checkout/record-step'
    private _getVendorTokenPQUrlPQ = 'api/vendor/pre-qualified-init'

    private _checkPersonalDetailsUrl = 'api/checkout/check-personal-details'
    private _submitPersonalDetailsV2URL = 'api/checkout/v2/submit-personal-details'
    private _submitAddressDetailsV2 = 'api/checkout/v2/submit-address-details'
    private _loadPersonalDetailsUrl = 'api/checkout/personal-details'
    private _loadAddressDetails = 'api/checkout/v2/personal-address-details'
    private _loadFinancialDetails = 'api/checkout/v2/personal-financial-details'

    private _countryCodebookUrl = 'api/codebook/countries'
    private _industryCodebookUrl = 'api/codebook/industries'
    private _nationalityCodebookUrl = 'api/codebook/nationalities'
    private _settlementsUrl = 'api/codebook/settlements'
    private _postZipBySettlementUrl = '/api/codebook/post-zips/settlement'
    private _streetsByPostZipUrl = '/api/codebook/streets/post-zip/'
    private _postZipsUrl = 'api/codebook/post-zips'
    private _settlementsByStreetUrl = '/api/codebook/settlements/street/'
    private _settlementsByPostZipUrl = '/api/codebook/settlements/post-zip'
    private _streetsBySettlementCodebookId = '/api/codebook/streets'
    private _companyListUrl = 'api/checkout/v2/company-list'

    private _createPinUrl = 'api/authenticate/pin/create'
    private _recreatePinUrl = 'api/authenticate/pin/recreate'
    private _changePinUrl = 'api/authenticate/pin/change'

    private _sessionCheckUrl = 'api/session-info'
    private _preQualifiedInfoUrl = 'api/checkout/pre-qualified/info'
    private _currencyUrl = 'api/currency'

    private _ibanUpdateUrl = 'api/profile/iban'
    private _changeMarketingConsentUrl = 'api/profile/marketing-consent'
    private _changeMarketingConsentLoginActionUrl = 'api/profile/marketing-consent/login-action'
    private _addressUpdateUrl = 'api/profile/address'

    private _paymentDataUrl = 'api/checkout/payment-data'
    private _getVendorTokenUrlCheckout = 'api/vendor/checkout-init'
    private _upsellCheckoutInitUrl = '/api/public/vendor/upsell/checkout-init'
    private _upsellOfferStateUrl = '/api/public/vendor/upsell/offer-state'
    private _gplCheckoutInitUrl = '/api/public/vendor/gpl/checkout-init'

    private _requestFinancialDataUrl = 'api/checkout/financial-data'

    private _validateOfferUrlV2 = 'api/checkout/v2/validate-offer'
    private _validateOfferDeclineReasonUrl = 'api/checkout/v1/validate-offer-result'
    private _contractDataUrl = 'api/checkout/contract-complex'
    private _pepQuestionnaireUrl = 'api/checkout/pep-questionnaire'
    private _pepQuestionnaireUrlROM = 'api/checkout/v1/pep-questionnaire'
    private _submitQuestionnaireUrl = 'api/checkout/questionnaire'
    private _pepStatusUrl = 'api/checkout/pep-status'
    private _signOrBuyUrl = 'api/checkout/transaction/sign-or-buy'
    private _signOrBuyGplUrl = 'api/checkout/sign-or-buy'
    private _invalidateVendorRequestUrl = 'api/checkout/invalidate-vendor-request'
    private _validateVendorRequestUrl = 'api/checkout/validate-vendor-request'
    private _cancelTransactionUrl = 'api/checkout/transaction/cancel'

    private _checkBankAccountNumber = 'api/checkout/v2/bank-account-number'
    private _ibanData = 'api/checkout/v1/iban-data'
    private _submitFinancialDetails = 'api/checkout/v2/financial-details'
    private _executionAmountsUrl = 'api/checkout/execution-amounts'
    private _loanAmountLimitsUrl = 'api/checkout/loan-amount-limits'
    private _purchaseLimitUrl = 'api/person/purchase-limit'
    private _planCalculationUrl = 'api/cash-flow/plan-calculation'

    private _getSelfCheckoutTokenUrl = 'api/self-checkout/offers'
    private _authorizationCode = 'api/self-checkout/authorization-code'
    private _authorizationCodeStatus = '/api/self-checkout/authorization-status'
    private _confirmAuthorizationCodePrompt = '/api/self-checkout/confirm-offer'

    private _validateVendorProductUrl = 'api/checkout/validate-vendor-product'

    private _acceptConsentUrl = 'api/consents'
    private _submitConsentsV2Url = 'api/v2/consents'
    private _submitOfferPlanDataUrl = 'api/application'
    private _confirmStateMachineFailureUrl = '/api/checkout/state-machine/confirmation'
    private _validateCreditTokenUrl = 'api/checkout/active'

    async getPurchaseLimit(token: string): Promise<CreditPurchaseRangeResponse> {
        const { data } = await this._httpClient.get<CreditPurchaseRangeResponse>(
            `${this._purchaseLimitUrl}?token=${token}`,
        )
        return data
    }

    // Returns the upsell offer state for a completed POS checkout (token = source vendor request token).
    async getUpsellOfferState(token: string): Promise<UpsellOfferStateDto> {
        const { data } = await this._httpClient.get<UpsellOfferStateDto>(
            this._upsellOfferStateUrl,
            { params: { token } },
        )
        return data
    }

    // SLO GPL (legacy MPL): Bearer-auth + token query param (BE resolves the vendor request).
    async getMplPurchaseLimit(token: string): Promise<CreditPurchaseRangeResponse> {
        const { data } = await this._httpClient.get<CreditPurchaseRangeResponse>(
            `${this._mplPurchaseLimitUrl}?token=${token}`,
        )
        return data
    }

    // SLO GPL (legacy MPL) post-sign final assessment status. Polling is owned by the caller
    // (legacy getCheckoutStatus retried 6x with a 5s delay while !isProcessed).
    async getMplCheckoutStatus(vendorRequestToken: string): Promise<MplCheckoutStatusResponse> {
        const { data } = await this._httpClient.get<MplCheckoutStatusResponse>(
            this._mplCheckoutStatusUrl,
            { params: { vendorRequestToken } },
        )
        return data
    }

    async calculatePaymentPlans(
        request: CreditPaymentPlanRequest,
    ): Promise<CreditPaymentDataResponse> {
        const { data } = await this._httpClient.post<CreditPaymentDataResponse>(
            this._planCalculationUrl,
            request,
        )
        return data
    }

    async validateCreditToken(token: string) {
        const { data } = await this._httpClient.get<CreditTokenValidationResponse>(
            `${this._validateCreditTokenUrl}?token=${token}`,
        )
        return data
    }

    async submitConsentsV2(request: ConsentV2Request) {
        const { data } = await this._httpClient.post(this._submitConsentsV2Url, request)
        return data
    }

    async submitOfferPlanData(token: string, request: OfferPlanDataRequest) {
        await this._httpClient.post(
            `${this._submitOfferPlanDataUrl}/${token}/steps/offer-plan-data`,
            request,
        )
    }

    async confirmStateMachineFailure(token: string) {
        const { data } = await this._httpClient.post(this._confirmStateMachineFailureUrl, {
            token,
        })
        return data
    }

    async getEligibilityValidationResult(): Promise<EligibilityValidationResponse> {
        const { data } = await this._httpClient.get<EligibilityValidationResponse>(
            this._eligibilityValidationResultUrl,
        )
        return data
    }

    async getOfferValidationResult(token: string): Promise<OfferValidationResultDTO> {
        const { data } = await this._httpClient.get<OfferValidationResultDTO>(
            `${this._offerValidationResultUrl}/${token}/validation-result`,
        )
        return data
    }

    async getCheckoutStateMachineStatus(token: string): Promise<CheckoutStateMachineStatusDto> {
        const { data } = await this._httpClient.get<CheckoutStateMachineStatusDto>(
            this._checkoutStateMachineStatusUrl,
            { params: { token } },
        )
        return data
    }

    // Covers the four backend-driven checks the SLO GPL flow runs after PLAN_DATA (VALIDATE_OFFER,
    // WATCHLIST, then the SCORING_DECISION_V1 → SCORING_CONFIRMATION scoring pair). A failed
    // watchlist check advances the state machine like a passing one, so it needs no branch here; a
    // declined offer settles on VALIDATE_OFFER_FAILURE, a negative scoring decision on
    // SCORING_DECISION_FAILURE and a failed scoring call on SCORING_REQUEST_FAILURE — legitimate
    // results rather than transport errors. checkoutRequestStatus/Type
    // stay null for MPL — only additionalInfo.stateMachineState is meaningful.
    async waitForBackendDrivenChecks(token: string): Promise<string> {
        for (let attempt = 0; attempt < BACKEND_CHECKS_POLL_MAX_ATTEMPTS; attempt++) {
            const { additionalInfo } = await this.getCheckoutStateMachineStatus(token)

            if (!BACKEND_DRIVEN_PENDING_STATES.includes(additionalInfo.stateMachineState)) {
                return additionalInfo.stateMachineState
            }

            await new Promise((resolve) => setTimeout(resolve, BACKEND_CHECKS_POLL_INTERVAL_MS))
        }

        throw new Error('BACKEND_CHECKS_TIMEOUT')
    }

    // 404 means no stored decision, which happens whenever VALIDATE_OFFER failed technically instead
    // of declining the offer — an expected outcome, so it resolves empty rather than rejecting.
    async getValidateOfferDeclineReason(
        token: string,
    ): Promise<ValidateOfferResultResponse | null> {
        try {
            const { data } = await this._httpClient.get<ValidateOfferResultResponse>(
                this._validateOfferDeclineReasonUrl,
                { params: { token } },
            )
            return data
        } catch (error) {
            if (isStandardErrorResponse(error) && error.status === 404) {
                return null
            }
            throw error
        }
    }

    // Replaces the dropped POST /v2/validate-offer and POST /v1/validation-result chain for SLO
    // GPL: the backend now runs every check through the validation step itself, so the frontend
    // waits the chain out and reads the confirmed plan instead of driving the last step.
    //
    // Every business outcome is returned rather than thrown — a thrown message cannot carry the
    // state machine's state, and the caller needs it to pick the screen and to decide whether the
    // failure still awaits confirmation. throw is reserved for the poll timeout and transport
    // errors, which are not outcomes.
    async runInitialAssessment(token: string, chosenPlan: PaymentPlan): Promise<InitialAssessment> {
        const state = await this.waitForBackendDrivenChecks(token)

        if (state === VALIDATE_OFFER_FAILURE_STATE) {
            return {
                outcome: 'DECLINED',
                reason: await this.resolveDeclineReason(token),
                stateMachineState: state,
            }
        }

        if (state === SCORING_DECISION_FAILURE_STATE) {
            return {
                outcome: 'DECLINED',
                reason: SCORING_DECISION_DECLINE_REASON,
                stateMachineState: state,
            }
        }

        if (state === SCORING_REQUEST_FAILURE_STATE) {
            return {
                outcome: 'DECLINED',
                reason: SCORING_REQUEST_FAILURE_REASON,
                stateMachineState: state,
            }
        }

        if (state === VALIDATION_RESULTS_FAILURE_STATE || state === ADJUSTED_PLAN_DATA_STATE) {
            return {
                outcome: 'DECLINED',
                reason: NO_POSSIBLE_PLAN_DECLINE_REASON,
                stateMachineState: state,
            }
        }

        const confirmed = await this.getOfferValidationResult(token)

        return { outcome: 'VALIDATED', plan: toConfirmedPaymentPlan(chosenPlan, confirmed) }
    }

    // The decline screen must render even when the reason cannot be read, so every failure to obtain
    // one degrades to the generic key the error screen already falls back to.
    private async resolveDeclineReason(token: string): Promise<string> {
        try {
            const result = await this.getValidateOfferDeclineReason(token)
            return result?.reasonKey ?? GENERIC_DECLINE_REASON
        } catch {
            return GENERIC_DECLINE_REASON
        }
    }

    async acceptConsent(request: AcceptConsentRequest) {
        const { data } = await this._httpClient.post(this._acceptConsentUrl, request)
        return data
    }

    //SELF CHECKOUT
    async getSelfCheckoutToken(request: GetSelfCheckoutTokenRequest) {
        const { data } = await this._httpClient.post<{ token: string }>(
            this._getSelfCheckoutTokenUrl,
            request,
        )

        return data
    }

    async getAuthorizationCode(token: string): Promise<AuthorizationCodeResponse> {
        const url = `${this._authorizationCode}?token=${token}`
        const { data } = await this._httpClient.get<AuthorizationCodeResponse>(url)
        return data
    }

    async getAuthorizationCodeStatus(token: string): Promise<AuthorizationStatusResponse> {
        const url = `${this._authorizationCodeStatus}?token=${token}`
        const { data } = await this._httpClient.get<AuthorizationStatusResponse>(url)
        return data
    }
    async confirmAuthorizationCodePrompt(payload: SecurityConfirmationRequest) {
        const { data } = await this._httpClient.post<any>(
            this._confirmAuthorizationCodePrompt,
            payload,
        )

        return data
    }

    //CHECKOUT
    async getPepQuestionnaireROM() {
        const { data } = await this._httpClient.get<QuestionnaireListDTO>(
            this._pepQuestionnaireUrlROM,
        )
        return {
            basicQuestionnaire: toQuestionnaire(data.basicQuestionnaire),
            additionalQuestionnaire: data.additionalQuestionnaire
                ? toQuestionnaire(data.additionalQuestionnaire)
                : undefined,
        }
    }

    async validateVendorProduct(payload: VendorProductValidationRequest) {
        const { data } = await this._httpClient.post<ValidateVendorResponse>(
            this._validateVendorProductUrl,
            payload,
        )
        return data
    }
    async getPepStatus(token: string) {
        const url = `${this._pepStatusUrl}/${token}`
        const { data } = await this._httpClient.get<'YES' | 'NO'>(url)
        return data
    }
    async getPepQuestionnaire() {
        const { data } = await this._httpClient.get<QuestionnaireDTO>(this._pepQuestionnaireUrl)
        return toQuestionnaire(data)
    }

    // Legacy SLO MPL: PEP questionnaire is submitted separately, before sign-or-buy.
    async submitQuestionnaire(payload: QuestionnaireRequest) {
        await this._httpClient.post<void>(this._submitQuestionnaireUrl, payload)
    }

    async signOrBuy(payload: ContractSignOrBuyRequest, twoFaPin?: string) {
        const { data } = await this._httpClient.post<SignOrBuyResponse>(
            this._signOrBuyUrl,
            payload,
            {
                headers: {
                    'X-LeanPayCoreApp-TwoFAPin': twoFaPin ?? '',
                },
            },
        )

        return data
    }

    async signOrBuyGpl(payload: ContractSignOrBuyRequest, twoFaPin?: string) {
        await this._httpClient.post<void>(this._signOrBuyGplUrl, payload, {
            headers: {
                'X-LeanPayCoreApp-TwoFAPin': twoFaPin ?? '',
            },
        })
    }
    async financialProductInit(payload: FinancialProductV2Request) {
        const { data } = await this._httpClient.post<FinancialProductV2Request>(
            this._financialProductInitUrl,
            payload,
        )
        return data
    }

    async invalidateVendorRequest(request: VendorRequestInvalidateRequest) {
        const { data } = await this._httpClient.post(this._invalidateVendorRequestUrl, request)

        return data
    }

    async validateVendorRequest(request: VendorRequestInvalidateRequest) {
        const { data } = await this._httpClient.post(this._validateVendorRequestUrl, request)

        return data
    }

    async getPaymentData(request: PaymentDataRequest) {
        const { data } = await this._httpClient.post<PaymentDataDTO>(this._paymentDataUrl, request)

        return toPaymentData(data)
    }

    async getMplPaymentData(request: MplPaymentDataRequest): Promise<MplPaymentData> {
        const { data } = await this._httpClient.post<MplPaymentDataWire>(
            this._mplPaymentDataUrl,
            request,
        )

        return {
            paymentData: mapMplPaymentDataToPaymentData(data),
            paymentCycleDateStrings: data.paymentCycleDates ?? [],
        }
    }

    async getVendorTokenCheckout(initToken: string) {
        const { data } = await this._httpClient.post<VendorApiInitResponse>(
            `${this._getVendorTokenUrlCheckout}?token=${initToken}`,
            {},
        )
        return data
    }

    async upsellCheckoutInit(token: string): Promise<UpsellCheckoutInitResponse> {
        const body: UpsellCheckoutInitRequest = { token }
        const { data } = await this._httpClient.post<UpsellCheckoutInitResponse>(
            this._upsellCheckoutInitUrl,
            body,
        )
        return data
    }

    async gplCheckoutInit(): Promise<GplCheckoutInitResponse> {
        const { data } = await this._httpClient.post<GplCheckoutInitResponse>(
            this._gplCheckoutInitUrl,
            {},
        )
        return data
    }

    async requestFinancialData(request: RequestFinancialDataRequest) {
        const { data } = await this._httpClient.post<any>(this._requestFinancialDataUrl, request)

        return data
    }

    async triggerWatchListCheck(token: string) {
        const { data } = await this._httpClient.post(this._amlWatchListUrl, { token })

        return data
    }

    async getStateMachineStatus(token: string) {
        const { data } = await this._httpClient.get<StateMachineDto>(
            `${this._stateMachineUrl}?entityId=${token}`,
        )
        return data
    }

    async validateCheckoutOfferV2(request: ValidationDataV2DTO) {
        const { data } = await this._httpClient.post<ValidationDataDTO>(
            this._validateOfferUrlV2,
            request,
        )

        const { applicationId } = data
        await this.initScoringCheck({ applicationId, token: request.token })

        const scoringStatus = await this.getApplicationScoringStatus(applicationId, request.token)

        if (scoringStatus.status === ScoringStatus.FAILED) {
            throw new Error('SCORING_FAILED')
        }

        const validateOfferData = await this.getValidateOfferResult({
            applicationId,
            token: request.token,
        })

        return validateOfferData
    }
    async getContractData(request: TransactionRequest) {
        const { data } = await this._httpClient.post<ContractDataComplexDTO[]>(
            this._contractDataUrl,
            request,
        )

        return data
    }
    //PREQUALIFY
    async getVendorTokenPQ(initTokenData?: VendorApiPreQualifiedRequest) {
        const { data } = await this._httpClient.post<VendorApiInitResponse>(
            this._getVendorTokenPQUrlPQ,
            undefined,
            {
                params: {
                    vendor: initTokenData?.vendor,
                    returnUrl: initTokenData?.returnUrl,
                    productCode: initTokenData?.productCode,
                    requestType: initTokenData?.requestType,
                },
            },
        )

        return data
    }

    async getPreQualifiedInfo(request: PreQualifiedInfoRequest) {
        const { data } = await this._httpClient.post<PreQualifiedInfoDTO>(
            this._preQualifiedInfoUrl,
            request,
        )

        return data
    }

    //COMMON

    async getValidateOfferResult(request: ValidationDataResultRequest) {
        const { data } = await this._httpClient.post<ValidationDataDTO>(
            this._validationResultUrl,
            request,
        )

        return toOfferValidation(data)
    }

    async getApplicationScoringStatus(applicationId: number, token: string) {
        const url = `${this._applicationScoringUrl}?applicationId=${applicationId}&token=${token}`
        const maxRetries = 36
        const retryInterval = 5000
        let timeoutId

        for (let i = 0; i < maxRetries; i++) {
            const { data } = await this._httpClient.get<ApplicationScoringResponse>(url)
            const status = data.status

            if (status === ScoringStatus.DONE) {
                clearTimeout(timeoutId!)
                return { status, applicationId }
            }

            if (status === ScoringStatus.FAILED) {
                clearTimeout(timeoutId!)
                return { status, applicationId }
            }

            // Wait for the specified interval before retrying
            await new Promise((resolve) => {
                timeoutId = setTimeout(resolve, retryInterval)
            })
        }

        throw Error('SCORING_FAILED')
    }

    async loadPersonalDetailsData(token: string) {
        const { data } = await this._httpClient.post<PersonalDetailsDTO>(
            this._loadPersonalDetailsUrl,
            {
                token: token,
            },
        )

        return data
    }
    async loadAddressDetails(token: string) {
        const url = `${this._loadAddressDetails}?token=${token}`
        const { data } = await this._httpClient.get<AddressDetailsResponse>(url)

        return toAddressData(data)
    }
    async loadFinancialDetails(token: string) {
        const url = `${this._loadFinancialDetails}?token=${token}`
        const { data } = await this._httpClient.get<FinancialDetailsResponse>(url)

        return data
    }

    async initScoringCheck(payload: ApplicationScoringRequest) {
        const { data } = await this._httpClient.post<ValidateOfferInitialResponse>(
            this._initScoringUrl,
            payload,
        )

        return data
    }

    async authenticate(payload: JWTLoginRequest): Promise<AuthResponse> {
        const res = await this._httpClient.post<AuthResponse>(this._authenticateUrl, payload)

        return res.data
    }

    // Authenticated: the caller's client session is what the issued code is bound to.
    async issueHandoff(vendorToken: string): Promise<HandoffIssueResponse> {
        const body: HandoffIssueRequest = { vendorToken }
        const { data } = await this._httpClient.post<HandoffIssueResponse>(
            this._issueHandoffUrl,
            body,
        )

        return data
    }

    // Anonymous endpoint: the caller must clear its auth token before calling, otherwise
    // clientInterceptor attaches the stale session it is about to replace.
    async exchangeHandoff(handoffCode: string): Promise<HandoffExchangeResponse> {
        const body: HandoffExchangeRequest = { handoffCode }
        const { data } = await this._httpClient.post<HandoffExchangeResponse>(
            this._exchangeHandoffUrl,
            body,
        )

        return data
    }

    async requestPIN(payload: AccountSingleStepPinRequest) {
        const { data } = await this._httpClient.post(this._requestPinUrl, payload)

        return data
    }

    async resendOtp(phoneNumber: string) {
        const request: AccountSingleStepPinRequest = {
            phoneNumber: phoneNumber,
        }

        const { data } = await this._httpClient.post(this._resendOtpUrl, request)
        return data
    }

    async resendOtpOverVoice(phoneNumber: string) {
        const request: AccountSingleStepPinRequest = {
            phoneNumber: phoneNumber,
        }

        const { data } = await this._httpClient.post(this._resendOtpOverVoiceUrl, request)

        return data
    }

    async checkPersonalDetails(payload: PersonalDetailsCheckRequest) {
        const { data } = await this._httpClient.post(this._checkPersonalDetailsUrl, payload)
        return data
    }

    async submitPersonalDetails(payload: PersonalDetailsV2Request) {
        const { data } = await this._httpClient.post(this._submitPersonalDetailsV2URL, payload)
        return data
    }

    async recordStep(payload: RecordStepParams) {
        try {
            // Prepare data string from data.
            let dataString: string = payload.data ? JSON.stringify(payload.data) : payload.data

            // Check if data string is to long.
            if (dataString && dataString.length > 40960) {
                dataString = dataString.substring(0, 40960)
            }

            const request: CheckoutStepRecordRequest = {
                token: payload.token,
                checkoutStepPriority: Date.now(),
                checkoutStep: payload.checkoutStep,
                checkoutStepViewType: payload.checkoutStepViewType,
                data: dataString,
            }

            const { data: res } = await this._httpClient.post(this._recordStepUrl, request)

            return res
        } catch {
            // Step telemetry is fire-and-forget — a failed recording must never
            // reject and break the user flow that triggered it.
            return undefined
        }
    }

    async getIndustryCodebook() {
        const { data } = await this._httpClient.get<any[]>(this._industryCodebookUrl)
        return data
    }
    async getCountryCodebook() {
        const { data } = await this._httpClient.get<CountryCodebookDTO[]>(this._countryCodebookUrl)
        return data
    }
    async getNationalityCodebook() {
        const { data } = await this._httpClient.get<NationalityCodebookDTO[]>(
            this._nationalityCodebookUrl,
        )
        return data
    }
    async getSettlements(countryCodebookId: string) {
        const url = `${this._settlementsUrl}?countryCodebookId=${countryCodebookId}`
        const { data } = await this._httpClient.get<SettlementCodebookDTO[]>(url)
        return data
    }

    async getSettlementsByStreet(streetId: string) {
        const url = this._settlementsByStreetUrl + streetId
        const { data } = await this._httpClient.get<Page<StreetCodebookDTO>>(url)

        return data.content
    }

    async getSettlementsByPostZip(postZipCodebookId: string) {
        const url = `${this._settlementsByPostZipUrl}/${postZipCodebookId}`
        const { data } = await this._httpClient.get<SettlementCodebookDTO[]>(url)

        return data
    }

    async getPostZips(countryCodebookId: string) {
        const url = `${this._postZipsUrl}?countryCodebookId=${countryCodebookId}`
        const { data } = await this._httpClient.get<PostZipCodebookDTO[]>(url)

        return data
    }
    async getPostZipsBySettlement(settlementCodebookId: string) {
        const url = `${this._postZipBySettlementUrl}/${settlementCodebookId}`
        const { data } = await this._httpClient.get<PostZipCodebookDTO[]>(url)

        return data
    }

    async getStreetsByPostZip(postZipCodebookId: string) {
        const url = `${this._streetsByPostZipUrl + postZipCodebookId}?size=10000`
        const { data } = await this._httpClient.get<Page<StreetCodebookDTO>>(url)

        return data.content
    }

    async getStreetsBySettlement(settlementCodebookId: string) {
        const url = `${this._streetsBySettlementCodebookId}?settlementCodebookId=${settlementCodebookId}`
        const { data } = await this._httpClient.get<StreetCodebookDTO[]>(url)

        return data
    }

    async getFinancialDetailsOptions(type?: FinancialDetailsOptionsTypes): Promise<string[]> {
        const url = `${this._financialTypes}/${type}`
        const { data } = await this._httpClient.get<any>(url)

        return data?.financialMetaValue
    }

    async getEmploymentTypes() {
        const { data } = await this._httpClient.get<EmploymentMetaTypeDTO[]>(
            this._employmentTypesUrl,
        )

        return data
    }
    async getMaritalStatuses() {
        const { data } = await this._httpClient.get<MaritalStatus[]>(this._maritalStatusesUrl)

        return data
    }

    async getCompaniesList(page: number, size: number, search: string) {
        const url = `${this._companyListUrl}?page=${page}&size=${size}${search ? `&search=${search}` : ''}`
        const res = await this._httpClient.get<Page<CompaniesListDTO>>(url)

        return {
            companies: res.data.content,
            totalPages: res.data.totalPages,
            totalElements: res.data.totalElements,
        }
    }

    async loadMaritalStatus() {
        const { data } = await this._httpClient.get<MaritalStatusResponse>(this._maritalStatusUrl)

        return data
    }

    async submitMaritalStatus(payload: MaritalStatusRequest) {
        const { data } = await this._httpClient.post(this._maritalStatusUrl, payload)

        return data
    }
    async submitEmploymentInfo(payload: EmploymentInfoRequest) {
        const { data } = await this._httpClient.post(this._submitEmploymentInfo, payload)

        return data
    }

    async checkFinancialDetails(payload: FinancialDetailsCheckRequest) {
        const { data } = await this._httpClient.post<FinancialDetailsValidationDataDTO>(
            this._checkFinancialDetailsUrl,
            payload,
        )

        return data
    }

    async createPin(request: CreatePinRequest) {
        const { data } = await this._httpClient.post(this._createPinUrl, request)

        return data
    }

    async recreatePin(request: ReCreatePinRequest) {
        const { data } = await this._httpClient.post(this._recreatePinUrl, request)

        return data
    }
    async changePin(request: ChangePinRequest) {
        const { data } = await this._httpClient.post(this._changePinUrl, request)

        return data
    }

    async getCurrencyFromServer() {
        const { data } = await this._httpClient.get<CurrencyInfo>(this._currencyUrl)

        return data
    }

    async getSessionInfo() {
        const { data } = await this._httpClient.get<SessionInfoDTO>(this._sessionCheckUrl)
        storage.save(STORAGE_KEY.SESSION_INFO, data)

        return data
    }

    async ibanUpdate(request: ProfileIbanChangeRequest, twoFaPin?: string) {
        await this._httpClient.post(this._ibanUpdateUrl, request, {
            headers: {
                'X-LeanPayCoreApp-TwoFAPin': twoFaPin ?? '',
            },
        })
    }

    async addressUpdate(request: ProfileAddressChangeRequest) {
        await this._httpClient.post(this._addressUpdateUrl, request)
    }

    async changeMarketingConsent(request: PersonMarketingConsentChangeRequest) {
        await this._httpClient.patch(this._changeMarketingConsentUrl, request)
    }

    async changeMarketingConsentLoginAction(request: PersonMarketingConsentChangeRequest) {
        await this._httpClient.patch(this._changeMarketingConsentLoginActionUrl, request)
    }

    async getLoanAmountLimits(request: LoanAmountLimitsRequest) {
        const { data } = await this._httpClient.get<any>(
            `${this._loanAmountLimitsUrl}?vendorId=${request.vendorId}`,
        )

        return data
    }

    async submitAddressDetailsV2(payload: AddressDetailsRequest) {
        const { data } = await this._httpClient.post<AddressDetailsRequest>(
            this._submitAddressDetailsV2,
            payload,
        )
        return data
    }

    async checkBankAccountNumber(payload: FinancialAccountNumberV2Request) {
        const { data } = await this._httpClient.post(this._checkBankAccountNumber, payload)

        return data
    }

    async getIbanData(token: string): Promise<IbanDataResponse> {
        const { data } = await this._httpClient.get<IbanDataResponse>(this._ibanData, {
            params: { token },
        })
        return data
    }

    async submitIbanData(payload: IbanDataRequest): Promise<void> {
        await this._httpClient.post(this._ibanData, payload)
    }
    async submitFinancialDetails(payload: FinancialDetailsRequestV2) {
        const { data } = await this._httpClient.post<FinancialDetailsRequestV2>(
            this._submitFinancialDetails,
            payload,
        )

        return data
    }

    async getExecutionAmounts() {
        const { data } = await this._httpClient.get<ExecutionAmountMetaDTO[]>(
            this._executionAmountsUrl,
        )

        const executionAmounts = data.sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1))
        return executionAmounts
    }

    async getFinancialData(token: string): Promise<FinancialDataResponse> {
        const url = `${this._requestFinancialDataUrl}/${token}`
        return new Promise((resolve, reject) => {
            const timeoutHandle = setTimeout(() => {
                return (async () => {
                    try {
                        const { data } = await this._httpClient.get<FinancialDataResponse>(url)

                        if (data.status === FinancialDataStatus.IN_PROCESSING) {
                            clearTimeout(timeoutHandle)
                            resolve(this.getFinancialData(token))
                        }

                        if (data.status === FinancialDataStatus.RESULTS_AVAILABLE) {
                            clearTimeout(timeoutHandle)
                            resolve(data)
                        }

                        if (data.status === FinancialDataStatus.RESULTS_INVALID) {
                            clearTimeout(timeoutHandle)
                            throw new Error(FinancialDataStatus.RESULTS_INVALID)
                        }

                        if (data.status === FinancialDataStatus.RESULTS_UNAVAILABLE) {
                            clearTimeout(timeoutHandle)
                            throw new Error(FinancialDataStatus.RESULTS_UNAVAILABLE)
                        }
                    } catch (error) {
                        clearTimeout(timeoutHandle)
                        reject(error)
                    }
                })()
            }, 5000)
        })
    }

    async getFinancialDataStatus(token: string): Promise<FinancialDataResponse> {
        const url = `${this._requestFinancialDataUrl}/${token}`
        const { data } = await this._httpClient.get<FinancialDataResponse>(url)
        return data
    }

    async cancelTransaction(payload: CancelTransactionRequest) {
        const { data } = await this._httpClient.post<any>(this._cancelTransactionUrl, payload)
        return data
    }
}

export const checkoutService = new CheckoutService()
